import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS } from '@shared';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import type { FileUploadedEvent } from '../../../analytics/analytics.events';
import { AnalyticsService } from '../../../analytics/analytics.service';
import * as fromFeatureFlags from '../../../feature-flags';
import * as fromLayout from '../../../layout';
import { MAX_USER_INPUT_LENGTH } from '../../chats.constants';
import { ChatsHelper } from '../../chats.helper';
import * as fromShared from '../../shared';
import type { SubmitDisabledResult } from '../../shared/selectors/conversation-prompt.selectors';
import { chatsFeature, ChatsState, ChatWithMessagesActions } from '../../store';
import * as fromMessagesListing from '../messages-listing';
import { SpeechToTextListeningComponent } from './speech-to-text';
import { SpeechToTextAzureService } from './speech-to-text/azure/speech-to-text-azure.service';
import { SpeechToTextDeepgramService } from './speech-to-text/deepgram';
import { SpeechToTextServiceInterface } from './speech-to-text/prompt-input-speech-to-text.interfaces';

const TEXTAREA_MIN_HEIGHT = 24;
const TEXTAREA_MAX_HEIGHT = 240;

@Component({
  selector: 'squadbox-conversation-prompt-input-smart',
  imports: [
    FormsModule,
    TranslateModule,
    MatTooltipModule,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.FileChipComponent,
    SpeechToTextListeningComponent,
    NgClass,
    fromShared.FileUploadTriggerDirective,
  ],
  template: `
    <div class="flex flex-col gap-4 w-full relative">
      <!-- CUSTOM TOOLTIP -->
      @let tooltipOverPrompt = tooltipOverPromptText();
      @if (tooltipOverPrompt) {
        <div
          class="
            place-items-center
            absolute -top-2.5 left-1/2 transform -translate-x-1/2 -translate-y-full
            mb-2 z-20
            max-w-full
          "
        >
          @if (tooltipOverPrompt) {
            <!-- Tooltip content -->
            <div
              class="
                px-2 py-1 bg-neutral-700 rounded tablet-portrait:max-w-70
                text-sm leading-5 font-semibold text-white text-center
              "
            >
              {{ tooltipOverPrompt | translate }}
            </div>
            <!-- Tooltip pointer -->
            <div
              class="
              w-2 h-2 bg-neutral-700 rotate-45 -z-1
              absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 -translate-y-full
            "
            ></div>
          }
        </div>
      }

      <!-- INPUT -->
      <div
        class="
          flex flex-col bg-shades-white tablet-landscape:shadow
          border border-neutral-200 rounded-3xl overflow-hidden
          transition-colors duration-200 ease-in-out
        "
        [ngClass]="{
          'hover:border-primary-300 focus-within:!border-primary-500':
            !isChatUnavailable(),
          '!bg-slate-50 !border-primary-500': sttState() !== 'inactive',
          'opacity-60': isChatUnavailable(),
        }"
      >
        <!-- SOURCES - FILE CHIPS -->
        @if (documentFileChips().length) {
          <div class="flex items-end justify-start pr-1 py-0">
            <div
              class="
                flex flex-wrap gap-2 grow basis-0 pl-2 pt-2
                max-h-24 overflow-x-clip overflow-y-auto
              "
            >
              @for (
                documentFileChip of documentFileChips();
                track documentFileChip.uuid
              ) {
                <sd-file-chip
                  [filename]="documentFileChip.filename"
                  [fileType]="documentFileChip.fileType"
                  [isLoading]="documentFileChip.isLoading"
                  [isError]="documentFileChip.error"
                  rightIcon="close"
                  (rightIconClicked)="removeFile(documentFileChip.uuid)"
                  (chipClicked)="openImageOverlayFromComposer(documentFileChip)"
                ></sd-file-chip>
              }
            </div>
          </div>
        }

        <!-- TEXTAREA -->
        <label class="block py-4 cursor-text">
          <textarea
            #textarea
            [value]="userInput()"
            (input)="handleInput($event)"
            (keydown)="handleKeydown($event)"
            (paste)="onPaste()"
            [disabled]="sttState() !== 'inactive' || isChatUnavailable()"
            placeholder="{{ inputPlaceholder() | translate }}"
            class="
              block min-h-6 max-h-80 w-full px-4 bg-transparent
              resize-none overflow-y-auto
              transition-[height] duration-200
              typo-p2 text-neutral-700 placeholder:text-neutral-500 leading-6
              focus:outline-hidden focus:caret-primary-500 disabled:cursor-not-allowed
            "
          ></textarea>
        </label>

        <div class="flex p-2 justify-between items-stretch">
          <!-- LEFT -->
          @if (sttState() !== 'inactive') {
            <button
              sdIconButton
              variant="tonal"
              color="neutral"
              class="fade-in bg-neutral-200 hover:bg-neutral-300 focus:bg-neutral-300 active:bg-neutral-400"
              [size]="isMobile() ? 'sm' : 'xs'"
              (click)="handleClickAbortSttCTA()"
              [disabled]="isAbortSttDisabled()"
              [matTooltip]="'PROMPT_INPUT.TOOLTIP.STOP' | translate"
              [matTooltipDisabled]="isAbortSttDisabled()"
              matTooltipPosition="above"
            >
              <svg sdUseUIBasicIcon="close"></svg>
            </button>
          } @else {
            <button
              #fileUploadButton
              sdIconButton
              squadboxFileUploadTrigger
              variant="icon"
              color="neutral"
              class="fade-in"
              [store]="fileUploadStore"
              [multiple]="remainingUploadSlots() > 1"
              [size]="isMobile() ? 'sm' : 'xs'"
              [matTooltip]="fileUploadTooltipText()"
              matTooltipPosition="above"
              [disabled]="fileUploadDisabled().disabled"
            >
              <svg sdUseUIBasicIcon="attach"></svg>
            </button>
          }

          <!-- MIDDLE -->
          <div
            class="flex-1 ml-2 mr-1 overflow-x-hidden flex items-center justify-end"
          >
            @if (
              sttState() === 'starting' ||
              sttState() === 'transcripting' ||
              sttState() === 'finalizing'
            ) {
              <squadbox-speech-to-text-listening
                class="fade-in"
                [isLoading]="sttState() !== 'transcripting'"
              ></squadbox-speech-to-text-listening>
            }
          </div>

          <!-- RIGHT -->
          <div class="flex justify-end items-center gap-3">
            @if (sttState() !== 'inactive') {
              <div
                class="fade-in min-w-10 font-small-text font-semibold text-caption text-right text-neutral-700 tabular-nums"
              >
                {{ recordingTimeLabel() }}
              </div>

              <button
                sdIconButton
                variant="filled"
                color="primary"
                [size]="isMobile() ? 'sm' : 'xs'"
                (click)="handleClickFinalizeSttCTA()"
                [disabled]="isFinalizeSttDisabled()"
                class="fade-in transition-all duration-300 ease-in-out"
              >
                <svg class="fade-in" sdUseUIBasicIcon="check"></svg>
              </button>
            } @else {
              <button
                sdIconButton
                class="fade-in"
                variant="icon"
                color="neutral"
                [size]="isMobile() ? 'sm' : 'xs'"
                [disabled]="isSpeechButtonDisabled()"
                (click)="handleClickStartSttCTA()"
                [matTooltip]="speechButtonTooltip() | translate"
                matTooltipPosition="above"
              >
                <svg sdUseUIBasicIcon="mic"></svg>
              </button>

              @let _submitTooltip = submitButtonTooltip();
              @let _submitDisabled = submitDisabledResult();
              <button
                sdIconButton
                variant="filled"
                color="primary"
                [size]="isMobile() ? 'sm' : 'xs'"
                [disabled]="_submitDisabled.disabled"
                (click)="handleClickSubmitPromptCTA()"
                class="fade-in transition-all duration-300 ease-in-out"
                [matTooltip]="
                  _submitDisabled.disabled && _submitDisabled.reasonKey
                    ? (_submitDisabled.reasonKey
                      | translate: _submitDisabled.reasonParams)
                    : _submitTooltip.i18nKey
                      ? (_submitTooltip.i18nKey
                        | translate: _submitTooltip.i18nParams)
                      : ''
                "
                matTooltipPosition="above"
              >
                <svg class="fade-in" sdUseArrowIcon="arrow-up"></svg>
              </button>
            }
          </div>
        </div>
      </div>
    </div>
  `,
})
export class ConversationPromptInputSmartComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  public readonly isThinking = input.required<boolean>();
  public readonly isNewReplyAnimating = input<boolean>(false);
  public readonly textarea =
    viewChild<ElementRef<HTMLTextAreaElement>>('textarea');
  public readonly fileUploadButton =
    viewChild<ElementRef<HTMLButtonElement>>('fileUploadButton');

  private readonly translateService = inject(TranslateService);
  private readonly featureFlagsStore = inject(
    Store<fromFeatureFlags.FeatureFlagsState>,
  );
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly chatsHelper = inject(ChatsHelper);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly azureSpeechToTextService =
    inject<SpeechToTextServiceInterface>(SpeechToTextAzureService);
  private readonly deepgramSpeechToTextService =
    inject<SpeechToTextServiceInterface>(SpeechToTextDeepgramService);
  private readonly sttService = computed(() => {
    if (this.ffDeepgram()) {
      return this.deepgramSpeechToTextService;
    }
    return this.azureSpeechToTextService;
  });
  public readonly sttState = computed(() => {
    return this.sttService().state();
  });
  private readonly selectedChatId = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChatId,
  );
  private readonly selectedChatAssistant = this.chatsStore.selectSignal(
    fromShared.selectSelectedChatAssistant,
  );
  private readonly isNewConversationWithAssistant =
    this.chatsStore.selectSignal(
      fromShared.selectIsNewConversationWithAssistant,
    );

  private readonly selectedChat = computed(() => {
    const selectedChat = this.chatsStore.selectSignal(
      chatsFeature.selectSelectedChat,
    )();
    if (!selectedChat) {
      return null;
    }
    return {
      ...selectedChat,
      title: this.chatsHelper.getChatTitle(selectedChat.title),
    };
  });
  private readonly selectedChatAssistantId = computed(
    () => this.selectedChat()?.assistantId ?? null,
  );
  private readonly isMessageTooLong = computed(() => {
    return this.userInput().length > MAX_USER_INPUT_LENGTH;
  });
  /**
   * This computed returns the documents associated with the selected chat grouped into:
   * - fromFileUpload: Files being uploaded
   * - fromChat: Documents attached to chat but not yet in messages
   * - fromUserChatMessages: Documents attached to the user's chat messages
   * - fromChatAssistant: Documents belonging to the chat's assistant
   */
  private readonly selectedChatDocuments = computed(() => ({
    fromFileUpload: this.fileUploadStore.getFiles(),
    ...this.chatsStore.selectSignal(fromShared.selectSelectedChatDocuments)(),
  }));
  private readonly localFilesUuids = computed(() =>
    this.fileUploadStore.getFiles().map((f) => f.uuid),
  );

  protected readonly isChatUnavailable = this.chatsStore.selectSignal(
    fromShared.selectIsChatUnavailable,
  );
  protected readonly limits = this.chatsStore.selectSignal(
    fromShared.selectDocumentLimits,
  );
  protected readonly remainingUploadSlots = computed(() =>
    this.chatsStore.selectSignal(
      fromShared.selectRemainingUploadSlots(this.localFilesUuids()),
    )(),
  );
  private readonly pendingChatDocumentsStatus = this.chatsStore.selectSignal(
    fromShared.selectPendingChatDocumentsStatus,
  );
  private readonly promptTextRequired = this.chatsStore.selectSignal(
    fromShared.selectIsPromptTextRequired,
  );
  private readonly promptOverlayTooltipFromStore = this.chatsStore.selectSignal(
    fromShared.selectPromptOverlayTooltip,
  );
  private readonly currentUploadRole = this.chatsStore.selectSignal(
    fromShared.selectCurrentUploadRole,
  );
  protected readonly documentCounts = computed(() =>
    this.chatsStore.selectSignal(
      fromShared.selectDocumentCounts(this.localFilesUuids()),
    )(),
  );
  protected readonly fileUploadDisabled = computed(() =>
    this.chatsStore.selectSignal(
      fromShared.selectFileUploadDisabled(this.localFilesUuids()),
    )(),
  );
  protected readonly promptInputHidden = this.chatsStore.selectSignal(
    fromShared.selectPromptInputHidden,
  );
  protected readonly submitButtonTooltip = computed(() =>
    this.chatsStore.selectSignal(
      fromShared.selectSubmitButtonTooltip(this.localFilesUuids()),
    )(),
  );
  private readonly supportedExtensions: string[] = [
    ...DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS,
  ];

  /** Args for the combined file-upload error toast selector (document + file-upload store errors). */
  private readonly fileUploadErrorToastArgs = computed(
    (): fromShared.FileUploadErrorToastSelectorArgs => {
      const files = this.fileUploadStore.getFiles();
      return {
        localFilesUuids: this.localFilesUuids(),
        fileUploadErrors: {
          // 'Empty file' (D4/SB-3437) folds into the generic upload-error toast:
          // a 0-byte pick is now rejected at pick time instead of uploading and
          // surfacing the vague post-upload failure path.
          hasUploadError: files.some(
            (f) =>
              f.error === 'Error uploading file' || f.error === 'Empty file',
          ),
          hasInvalidType: files.some((f) => f.error === 'Invalid file type'),
          hasSizeExceeded: files.some((f) => f.error === 'File size exceeded'),
        },
        supportedExtensions: this.supportedExtensions,
        maxFileSizeMB: environment.maxFileUploadSizeInMB,
      };
    },
  );

  /**
   * State of the file upload error toast (document count/limits/processing + upload/type/size errors).
   * Selector holds all logic; component only translates and builds visibility/dismissable.
   */
  public readonly fileUploadErrorToastState = computed(() => {
    const messages = this.chatsStore.selectSignal(
      fromShared.selectFileUploadErrorToastMessages(
        this.fileUploadErrorToastArgs(),
      ),
    )();
    const translated = messages.map(
      (toast) =>
        this.translateService.instant(
          toast.i18nKey,
          toast.i18nParams,
        ) as string,
    );
    return {
      isVisible: translated.length > 0,
      message: translated.join(' '),
      dismissable: this.promptInputHidden(),
    };
  });

  public readonly fileUploadStore = inject(fromShared.FileUploadV2Store);
  private readonly imagePreview = inject(fromShared.ChatImagePreviewStore);
  public readonly ffDeepgram = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectFeatureFlag('ff-deepgram'),
  );
  public readonly isFileUploadLoading = computed(() => {
    return (
      this.fileUploadStore.state().loading ||
      this.pendingChatDocumentsStatus().hasProcessing
    );
  });

  public readonly tooltipOverPromptText = computed(() => {
    const overlay = this.promptOverlayTooltipFromStore();
    if (overlay.i18nKey) return overlay.i18nKey;
    if (this.fileUploadStore.state().loading)
      return 'PROMPT_INPUT.TOOLTIP.ADD_SOURCE_PROCESSING_FILE';
    if (this.isMessageTooLong()) return 'PROMPT_INPUT.TOOLTIP.MESSAGE_TOO_LONG';
    return null;
  });
  private readonly fileUploadTooltip = computed(() =>
    this.chatsStore.selectSignal(
      fromShared.selectFileUploadDisabled(this.localFilesUuids()),
    )(),
  );
  public readonly fileUploadTooltipText = computed(() => {
    const tip = this.fileUploadTooltip();
    return tip.i18nKey
      ? (this.translateService.instant(tip.i18nKey, tip.i18nParams) as string)
      : '';
  });
  /** Submit disabled state plus reason (for tooltip/a11y). First matching condition wins. */
  public readonly submitDisabledResult = computed((): SubmitDisabledResult => {
    const counts = this.documentCounts();
    const limits = this.limits();
    const filesFromUpload = this.selectedChatDocuments().fromFileUpload;
    const notDisabled: SubmitDisabledResult = {
      disabled: false,
      reasonKey: '',
      reason: 'Submit allowed',
    };

    if (this.isChatUnavailable()) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.CHAT_UNAVAILABLE',
        reasonParams: {},
        reason: 'Chat is unavailable — assistant is not in ready status',
      };
    }
    if (this.promptTextRequired() && this.userInput().trim() === '') {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.MESSAGE_REQUIRED',
        reasonParams: {},
        reason: 'Prompt text is required but empty',
      };
    }
    if (this.isFileUploadLoading()) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.ADD_SOURCE_PROCESSING_FILE',
        reasonParams: {},
        reason: 'Files are still uploading or documents are processing',
      };
    }
    if (this.isMessageTooLong()) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.MESSAGE_TOO_LONG',
        reasonParams: {},
        reason: 'Message exceeds maximum length',
      };
    }
    if (this.isThinking()) {
      return {
        disabled: true,
        reasonKey: 'CHAT.LOADING',
        reasonParams: {},
        reason: 'Assistant is thinking',
      };
    }
    if (this.isNewReplyAnimating()) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.PLEASE_WAIT',
        reasonParams: {},
        reason: 'Please wait for the reply to finish',
      };
    }
    if (counts.inCurrentPhase > limits.max) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.ADD_SOURCE_FILE_LIMIT_REACHED',
        reasonParams: {},
        reason: `Document count (${counts.inCurrentPhase}) exceeds maximum (${limits.max})`,
      };
    }
    if (counts.inCurrentPhase < limits.min) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.ADD_SOURCE_FILE_UNDER_MIN_FILES',
        reasonParams: { minFiles: limits.min },
        reason: `Need at least ${limits.min} documents, have ${counts.inCurrentPhase}`,
      };
    }
    if (this.pendingChatDocumentsStatus().hasErrors) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.ADD_SOURCE_PROCESSING_ERROR',
        reasonParams: {},
        reason: 'One or more documents failed processing',
      };
    }
    if (
      filesFromUpload.some(
        (file) =>
          file.status === 'upload-invalid' || file.status === 'upload-failed',
      )
    ) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.ADD_SOURCE_UPLOAD_ERROR',
        reasonParams: {},
        reason: 'One or more files failed to upload',
      };
    }
    return notDisabled;
  });

  public readonly inputPlaceholder: Signal<string> = computed(() => {
    switch (this.sttState()) {
      case 'starting':
        return 'PROMPT_INPUT.STT_STARTING';
      case 'transcripting':
        return 'PROMPT_INPUT.STT_LISTENING';
      case 'finalizing':
        return 'PROMPT_INPUT.STT_FINALIZING';
      default:
        return this.selectedChatAssistant()?.trait === 'comparison' &&
          this.isNewConversationWithAssistant()
          ? 'ASSISTANTS.TRAITS.COMPARISON.PLACEHOLDER_INPUT_TEXT'
          : 'PROMPT_INPUT.PLACEHOLDER';
    }
  });
  public readonly isSpeechButtonDisabled = computed(
    () => this.isChatUnavailable() || this.userInput().trim() !== '',
  );
  public readonly speechButtonTooltip = computed(() => {
    return this.isSpeechButtonDisabled()
      ? ''
      : 'PROMPT_INPUT.TOOLTIP.VOICE_TO_TEXT';
  });
  /**
   * Determines if the abort STT button should be disabled.
   * Enabled only when STT is actively processing: 'starting', 'transcripting', or 'finalizing'.
   * Disabled in transient/cleanup states like 'inactive', 'error', 'aborting', or 'completed'
   * since these states are already cleaning up and don't require user intervention.
   */
  public readonly isAbortSttDisabled = computed(() => {
    const state = this.sttState();
    return (
      state !== 'starting' &&
      state !== 'transcripting' &&
      state !== 'finalizing'
    );
  });
  /**
   * Determines if the finalize STT button should be disabled.
   * Enabled when state is 'transcripting' (normal case).
   */
  public readonly isFinalizeSttDisabled = computed(
    () => this.sttState() !== 'transcripting',
  );
  public readonly documentFileChips = computed(() => {
    const fromChatDocs = this.selectedChatDocuments().fromChat;
    const fromFileUploadFiles = this.selectedChatDocuments().fromFileUpload;
    const chips: {
      uuid: string;
      filename: string;
      fileType: fromSeriousSystem.FileTypeIconsEnum;
      isLoading: boolean;
      error: boolean;
      type: 'document' | 'file';
    }[] = [];

    fromChatDocs.forEach((document) => {
      chips.push({
        uuid: document.uuid,
        filename: document.filename,
        fileType: fromSeriousSystem.getFileTypeIconFromExtension(
          document.extension,
        ),
        isLoading: document.status === 'processing',
        type: 'document',
        error: document.status === 'failed',
      });
    });

    fromFileUploadFiles
      .filter((file) => !fromChatDocs.some((doc) => doc.uuid === file.uuid))
      .forEach((file) => {
        chips.push({
          uuid: file.uuid,
          filename: file.name,
          fileType: fromSeriousSystem.getFileTypeIconFromExtension(
            file.extension,
          ),
          isLoading:
            file.status === 'upload-ongoing' ||
            file.status === 'upload-pending',
          type: 'file',
          error:
            file.status === 'upload-invalid' || file.status === 'upload-failed',
        });
      });

    return chips;
  });

  public readonly removedPromptInputState = computed(() => {
    const documents = this.documentFileChips();
    const shouldRemovePromptInput = this.promptInputHidden();
    const limits = this.limits();

    if (!shouldRemovePromptInput) {
      return {
        shouldRemovePromptInput: false as const,
        shouldShowTransientFirstMessageWithDocuments: false as const,
      } as const;
    }

    const hasLoadingDocuments = documents.some((doc) => doc.isLoading);
    const hasDocumentErrors = documents.some((doc) => doc.error);
    const hasOnlyValidDocuments =
      documents.every((doc) => doc.type === 'document') && !hasDocumentErrors;
    const hasDocumentsCountWithinLimits =
      documents.length >= limits.min && documents.length <= limits.max;

    return {
      shouldRemovePromptInput,
      shouldShowTransientFirstMessageWithDocuments: true as const,
      shouldShowChatbotThinkingForDocuments:
        hasOnlyValidDocuments && hasDocumentsCountWithinLimits,
      shouldAutoSubmitMessageWithDocuments:
        !hasLoadingDocuments &&
        hasOnlyValidDocuments &&
        hasDocumentsCountWithinLimits,
      message: {
        id: -1,
        content: '' as const,
        isFromUser: true as const,
        documents: documents.map((doc) => ({
          uuid: doc.uuid,
          filename: doc.filename,
          extension: doc.fileType,
          deletedAt: null,
          status: this.getDocumentStatus(doc),
          isError: doc.error ?? false,
          isLoading: doc.type === 'file' && doc.isLoading,
          isDisabled: false as const,
          isClickable: false as const,
          type: doc.type,
        })),
      } satisfies fromMessagesListing.MessageListingType,
    };
  });

  private readonly inputSubject = new Subject<void>();
  public readonly userInput = signal('');

  // Recording timer state
  private readonly recordingSeconds = signal(0);
  private recordingIntervalId: NodeJS.Timeout | null = null;
  public readonly recordingTimeLabel = computed(() => {
    const totalSeconds = this.recordingSeconds();
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  });

  public readonly isMobile = inject<Store<fromLayout.LayoutState>>(
    Store,
  ).selectSignal(fromLayout.layoutFeature.selectIsMobile);

  private transcriptionCompletedSubscription: Subscription | null = null;
  private transcriptResultSubscription: Subscription | null = null;
  private readonly ffSttDebugLog = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectFeatureFlag<boolean>(
      'ff-stt-debug-log',
    ),
  );

  constructor() {
    effect(() => {
      this.transcriptionCompletedSubscription?.unsubscribe();
      this.transcriptionCompletedSubscription =
        this.sttService().transcriptionCompleted.subscribe(({ raw, clean }) => {
          if (this.ffSttDebugLog()) {
            // eslint-disable-next-line no-console
            console.log('STT Debug Log - transcriptionCompleted', {
              raw,
              clean,
              previousInput: this.userInput(),
              newInput: this.capitalizeFirstLetter(clean),
            });
          }
          this.userInput.set(this.capitalizeFirstLetter(clean));
          setTimeout(() => this.adjustTextareaHeight(), 100);
          this.stopSttTimer();
        });

      this.transcriptResultSubscription?.unsubscribe();
      this.transcriptResultSubscription =
        this.sttService().streamResult.subscribe((result) => {
          if (this.ffDeepgram() && result.transcript) {
            this.userInput.set(this.capitalizeFirstLetter(result.transcript));
            setTimeout(() => this.adjustTextareaHeight(), 100);
          }
        });
    });

    /**
     * When the selected chat or assistant changes:
     * - Resets the file upload state
     * - Clears the user input
     * - Removes any existing documents from the chat
     */
    effect(() => {
      const chatId = this.selectedChatId();

      this.selectedChatAssistantId();
      this.fileUploadStore.reset();
      this.imagePreview.reset();
      this.clear();

      untracked(() => {
        const chatDocuments = this.selectedChatDocuments().fromChat;
        if (chatId && chatDocuments.length > 0) {
          this.chatsStore.dispatch(
            ChatWithMessagesActions.removeDocuments({
              chatId,
            }),
          );
        }
      });
    });

    /**
     * Attaches files to the chat as documents when:
     * - the file-upload state is valid and
     * - the file-upload state files are all uploaded
     * - the chat has an id
     * then, removes the files from the file upload store.
     */
    /**
     * Capture a same-session preview blob for each staged IMAGE
     * file the moment it appears (idempotent), so the overlay (and, later, the
     * in-thread thumbnail) can render it across the ingestion gap. Must run
     * before the upload-done effect below drops the raw File.
     */
    effect(() => {
      this.fileUploadStore.getFiles().forEach((file) => {
        if (file.file && this.isImageExtension(file.extension)) {
          this.imagePreview.capture(file.uuid, file.file);
        }
      });
    });

    effect(() => {
      const uploadedFiles = this.fileUploadStore
        .getFiles()
        .filter((file) => file.status === 'upload-done');
      if (!uploadedFiles.length) return;

      untracked(() => {
        const chatId = this.selectedChatId();
        if (!chatId) return;

        const documentRole = this.currentUploadRole();

        uploadedFiles.forEach((file) => {
          // A completed chat upload always carries its real transaction id;
          // `null` only ever marks a builder-seeded persisted doc (never present
          // on this chat store), so skipping it is defensive, not reachable here.
          if (file.transactionId === null) return;
          this.chatsStore.dispatch(
            ChatWithMessagesActions.addDocuments({
              transactionId: file.transactionId,
              chatId,
              assistantUuid: this.isNewConversationWithAssistant()
                ? this.selectedChatAssistantId()
                : null,
              files: [file],
              documentRole,
            }),
          );
        });
        this.fileUploadStore.removeFiles(
          uploadedFiles.map((file) => file.uuid),
        );
      });
    });

    /**
     * Set the focus on the textarea (prompt-input)
     * - when the chatbot stops thinking
     * - when the selected assistant or chat is changed
     */
    effect(() => {
      this.selectedChatId();
      this.selectedChatAssistantId();

      setTimeout(() => {
        if (this.textarea() && !this.isThinking()) {
          this.textarea()?.nativeElement.focus();
        }
      }, 0);
    });

    /**
     * Effect: When files have been uploaded (documents are present in fromChat, regardless of whether they're still
     * 'processing' or already 'ready'), and the assistant's prompt input is hidden (as determined by removedPromptInputState),
     * automatically create and submit a new empty message with those documents attached.
     *
     * - The user does not interact—submission is triggered automatically; no prompt content is required.
     */
    effect(() => {
      const removedPromptInputState = this.removedPromptInputState();
      if (
        'shouldAutoSubmitMessageWithDocuments' in removedPromptInputState &&
        removedPromptInputState.shouldAutoSubmitMessageWithDocuments
      ) {
        this.addChatMessage('');
      }
    });
  }

  ngOnInit() {
    this.inputSubject.pipe(debounceTime(50)).subscribe(() => {
      setTimeout(() => this.adjustTextareaHeight(), 0);
    });
  }

  ngAfterViewInit() {
    this.adjustTextareaHeight();
  }

  ngOnDestroy() {
    this.transcriptionCompletedSubscription?.unsubscribe();
    this.transcriptResultSubscription?.unsubscribe();
    this.abortStt();
  }

  public handleInput(event: Event) {
    if (this.sttState() === 'inactive') {
      this.userInput.set((event.target as HTMLTextAreaElement).value);
      this.inputSubject.next();
    }
  }

  public onPaste() {
    setTimeout(() => this.adjustTextareaHeight(), 0);
  }

  public adjustTextareaHeight(reset = false) {
    const textarea = this.textarea()?.nativeElement;
    if (!textarea) return;

    if (reset || !textarea.value) {
      textarea.style.height = `${TEXTAREA_MIN_HEIGHT}px`;
      return;
    }

    // Create a hidden clone to measure content
    const clone = textarea.cloneNode(true) as HTMLTextAreaElement;
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.height = 'auto';
    clone.style.minHeight = `${TEXTAREA_MIN_HEIGHT}px`;
    clone.style.maxHeight = 'none';
    clone.style.transition = 'none';
    clone.style.transitionDuration = '0s';
    clone.style.width = `${textarea.clientWidth}px`;
    clone.rows = 1;
    clone.value = textarea.value;

    document.body.appendChild(clone);
    const contentHeight = clone.scrollHeight;
    clone.remove();

    if (contentHeight < TEXTAREA_MAX_HEIGHT) {
      textarea.classList.add('hide-scrollbar');
    } else {
      textarea.classList.remove('hide-scrollbar');
    }

    const newHeight = Math.min(contentHeight, TEXTAREA_MAX_HEIGHT);
    textarea.style.height = `${newHeight}px`;

    if (this.sttState() === 'transcripting') {
      textarea.scrollTop = textarea.scrollHeight;
    }
  }

  private submitPrompt() {
    this.addChatMessage(this.userInput());
    this.clear();
  }

  public handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!this.submitDisabledResult().disabled) {
        this.submitPrompt();
      }
    }
  }

  public handleClickSubmitPromptCTA() {
    if (!this.submitDisabledResult().disabled) {
      this.submitPrompt();
    }
  }

  public clear() {
    this.userInput.set('');
    this.adjustTextareaHeight(true);
  }

  private capitalizeFirstLetter(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  public removeFile(uuid: string) {
    const chatId = this.selectedChatId();
    if (!chatId) {
      return;
    }
    this.chatsStore.dispatch(
      ChatWithMessagesActions.removeDocument({
        chatId,
        documentUuid: uuid,
      }),
    );
    this.fileUploadStore.removeFile(uuid);
    // Drop the same-session preview blob for a chip removed before send.
    this.imagePreview.release(uuid);
  }

  private isImageExtension(extension: string): boolean {
    return (
      fromSeriousSystem.getFileTypeIconFromExtension(extension) ===
      fromSeriousSystem.FileTypeIconsEnum.image
    );
  }

  /**
   * Open the full-screen overlay for a staged image chip.
   * No-op for non-image chips — a document chip has no pre-send preview.
   * The chip's × (remove) stops propagation, so this does not fire on remove.
   */
  public openImageOverlayFromComposer(chip: {
    uuid: string;
    filename: string;
    fileType: fromSeriousSystem.FileTypeIconsEnum;
  }) {
    if (chip.fileType !== fromSeriousSystem.FileTypeIconsEnum.image) {
      return;
    }
    this.chatsStore.dispatch(
      fromShared.ImageOverlayActions.openImageFromComposer({
        uuid: chip.uuid,
        filename: chip.filename,
      }),
    );
    // Track overlay open from a composer image chip.
    this.analyticsService
      .sendImageOverlayOpenedEvent({ source: 'chip' })
      .subscribe();
  }

  public addChatMessage(content: string) {
    const selectedChat = this.selectedChat();
    const selectedChatAssistant = this.selectedChatAssistant();

    if (!selectedChat) {
      return;
    }

    const dispatchChatMessage = () => {
      this.chatsStore.dispatch(
        ChatWithMessagesActions.addChatMessage({
          chatId: selectedChat.id,
          content,
          assistant:
            selectedChatAssistant && !selectedChat._count?.messages
              ? {
                  uuid: selectedChatAssistant.uuid,
                  description: selectedChatAssistant.description,
                  introMessage: selectedChatAssistant.introMessage,
                  locale: selectedChatAssistant.locale,
                }
              : undefined,
          documents: selectedChat.documents,
        }),
      );
    };

    if (!document.startViewTransition) {
      dispatchChatMessage();
      return;
    }

    document.startViewTransition(() => {
      dispatchChatMessage();
    });
  }

  private startSttTimer(): void {
    this.clearSttInterval();
    this.recordingSeconds.set(0);
    this.recordingIntervalId = setInterval((): void => {
      this.recordingSeconds.update((v) => v + 1);
    }, 1000);
  }

  private stopSttTimer(): void {
    this.clearSttInterval();
    this.recordingSeconds.set(0);
  }

  private clearSttInterval(): void {
    if (this.recordingIntervalId !== null) {
      clearInterval(this.recordingIntervalId);
      this.recordingIntervalId = null;
    }
  }

  public handleClickStartSttCTA() {
    void this.sttService().start();
    this.startSttTimer();
  }

  public handleClickFinalizeSttCTA() {
    this.sttService().finalize();
  }

  public handleClickAbortSttCTA() {
    this.userInput.set('');
    setTimeout(() => this.adjustTextareaHeight(), 100);
    this.abortStt();
  }

  private abortStt() {
    this.sttService().abort();
    this.stopSttTimer();
  }

  public triggerFileUpload(from: FileUploadedEvent['from']) {
    this.fileUploadStore.setUploadFrom(from);
    this.fileUploadButton()?.nativeElement.click();
  }

  private getDocumentStatus(doc: {
    error: boolean;
    isLoading: boolean;
    type: 'document' | 'file';
  }): 'failed' | 'processing' | 'ready' {
    if (doc.error) return 'failed';
    if (doc.isLoading) return 'processing';
    return 'ready';
  }
}
